import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { roomService, tableService, tableQrService } from '../services/api';

interface Room { id: number; name: string; sort_order: number; }
interface DiningTable { id: number; room_id: number; room_name: string; name: string; seats: number; is_active: number; }

const Page = styled.div`max-width: 800px; margin: 0 auto; display: flex; flex-direction: column; gap: 24px;`;
const Title = styled.h1`font-size: 22px; font-weight: 700; color: #111827; margin: 0;`;

const ErrorBanner = styled.div`
  background: #fef2f2; border: 1px solid #fca5a5; color: #dc2626;
  padding: 10px 14px; border-radius: 8px; font-size: 13px; cursor: pointer;
`;

const Card = styled.div`background: white; border-radius: 12px; box-shadow: 0 1px 4px rgba(0,0,0,0.08); overflow: hidden;`;
const CardHeader = styled.div`
  padding: 14px 20px; border-bottom: 1px solid #f3f4f6;
  display: flex; align-items: center; justify-content: space-between;
`;
const CardTitle = styled.h2`font-size: 15px; font-weight: 600; color: #111827; margin: 0;`;

const Row = styled.div`
  display: flex; align-items: center; gap: 12px;
  padding: 11px 20px; border-bottom: 1px solid #f9fafb;
  &:last-child { border-bottom: none; }
`;
const RowName = styled.span`flex: 1; font-size: 14px; font-weight: 600; color: #1f2937;`;
const RowMeta = styled.span`font-size: 12px; color: #9ca3af;`;
const RowActions = styled.div`display: flex; gap: 6px;`;

const Btn = styled.button<{ $variant?: 'primary' | 'danger' | 'ghost' | 'qr' | 'warn' }>`
  padding: 5px 12px; border-radius: 6px; border: none;
  font-size: 12px; font-weight: 500; cursor: pointer;
  background: ${p =>
    p.$variant === 'danger' ? '#fee2e2' :
    p.$variant === 'primary' ? '#3b82f6' :
    p.$variant === 'qr' ? '#f0fdf4' :
    p.$variant === 'warn' ? '#fef3c7' : '#f3f4f6'};
  color: ${p =>
    p.$variant === 'danger' ? '#dc2626' :
    p.$variant === 'primary' ? 'white' :
    p.$variant === 'qr' ? '#16a34a' :
    p.$variant === 'warn' ? '#b45309' : '#374151'};
  &:hover { opacity: 0.85; }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

const InactiveBadge = styled.span`
  font-size: 11px; font-weight: 600; color: #b45309;
  background: #fef3c7; padding: 2px 8px; border-radius: 20px;
`;

const Form = styled.form`
  padding: 14px 20px; display: flex; gap: 8px; flex-wrap: wrap;
  border-top: 1px solid #f3f4f6; background: #fafafa;
`;
const Input = styled.input`
  flex: 1; min-width: 140px; padding: 7px 12px;
  border: 1px solid #d1d5db; border-radius: 6px; font-size: 14px;
  &:focus { outline: none; border-color: #3b82f6; }
`;
const Select = styled.select`
  padding: 7px 12px; border: 1px solid #d1d5db; border-radius: 6px;
  font-size: 14px; background: white;
  &:focus { outline: none; border-color: #3b82f6; }
`;
const NumInput = styled(Input)`flex: none; width: 72px;`;

const EmptyRow = styled.div`padding: 20px; text-align: center; color: #9ca3af; font-size: 14px;`;

const InlineEdit = styled.form`display: flex; flex: 1; gap: 6px; align-items: center;`;
const InlineInput = styled.input`
  flex: 1; padding: 5px 10px; border: 1px solid #93c5fd; border-radius: 6px; font-size: 14px;
  &:focus { outline: none; }
`;

export default function RoomsManager() {
  const [rooms, setRooms] = useState<Room[]>([]);
  const [tables, setTables] = useState<DiningTable[]>([]);
  const [newRoom, setNewRoom] = useState('');
  const [newTable, setNewTable] = useState({ room_id: '', name: '', seats: '4' });
  const [editRoom, setEditRoom] = useState<Room | null>(null);
  const [editTable, setEditTable] = useState<DiningTable | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const reload = async () => {
    try {
      const [r, t] = await Promise.all([roomService.getAll(), tableService.getAll()]);
      setRooms(r.data?.rooms || []);
      setTables(t.data?.tables || []);
    } catch (err: any) {
      setError(`Errore: ${err?.response?.data?.message || err?.message}`);
    }
  };

  useEffect(() => { reload().finally(() => setLoading(false)); }, []);

  const handleAddRoom = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newRoom.trim()) return;
    setSaving(true); setError(null);
    try {
      const res = await roomService.create({ name: newRoom.trim() });
      if (!res.success) throw new Error(res.message);
      setNewRoom('');
      await reload();
    } catch (err: any) {
      setError(`Errore: ${err?.response?.data?.message || err?.message}`);
    } finally { setSaving(false); }
  };

  const handleSaveRoom = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editRoom) return;
    try {
      await roomService.update(editRoom.id, { name: editRoom.name.trim() });
      setEditRoom(null); await reload();
    } catch (err: any) { setError(`Errore: ${err?.message}`); }
  };

  const handleDeleteRoom = async (id: number, name: string) => {
    if (!window.confirm(`Eliminare la sala "${name}"? Anche tutti i suoi tavoli verranno eliminati.`)) return;
    try { await roomService.delete(id); await reload(); }
    catch (err: any) { setError(`Errore: ${err?.message}`); }
  };

  const handleAddTable = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newTable.room_id) { setError('Seleziona una sala'); return; }
    if (!newTable.name.trim()) { setError('Inserisci il numero o nome del tavolo'); return; }
    setSaving(true); setError(null);
    try {
      const res = await tableService.create({
        room_id: parseInt(newTable.room_id),
        name: newTable.name.trim(),
        seats: parseInt(newTable.seats) || 4,
      });
      if (!res.success) throw new Error(res.message);
      setNewTable(prev => ({ ...prev, name: '' }));
      await reload();
    } catch (err: any) {
      setError(`Errore: ${err?.response?.data?.message || err?.message}`);
    } finally { setSaving(false); }
  };

  const handleSaveTable = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editTable) return;
    try {
      await tableService.update(editTable.id, { name: editTable.name.trim(), seats: editTable.seats });
      setEditTable(null); await reload();
    } catch (err: any) { setError(`Errore: ${err?.message}`); }
  };

  const handleToggleTableActive = async (table: DiningTable) => {
    const turningOff = table.is_active !== 0;
    if (turningOff && !window.confirm(`Disattivare il tavolo ${table.name}? Il QR resta valido ma il tavolo non sarà ordinabile finché non lo riattivi.`)) return;
    try { await tableService.update(table.id, { is_active: turningOff ? 0 : 1 }); await reload(); }
    catch (err: any) { setError(`Errore: ${err?.message}`); }
  };

  if (loading) return <Page><p style={{ color: '#6b7280' }}>Caricamento...</p></Page>;

  return (
    <Page>
      <Title>Sale & Tavoli</Title>
      {error && <ErrorBanner onClick={() => setError(null)}>{error} &times;</ErrorBanner>}

      {/* SALE */}
      <Card>
        <CardHeader><CardTitle>🏠 Sale</CardTitle></CardHeader>
        {rooms.length === 0 ? (
          <EmptyRow>Nessuna sala — aggiungila qui sotto</EmptyRow>
        ) : rooms.map(room => (
          <Row key={room.id}>
            {editRoom?.id === room.id ? (
              <InlineEdit onSubmit={handleSaveRoom}>
                <InlineInput autoFocus value={editRoom.name} onChange={e => setEditRoom({ ...editRoom, name: e.target.value })} />
                <Btn $variant="primary" type="submit">Salva</Btn>
                <Btn type="button" onClick={() => setEditRoom(null)}>Annulla</Btn>
              </InlineEdit>
            ) : (
              <>
                <RowName>{room.name}</RowName>
                <RowMeta>{tables.filter(t => t.room_id === room.id).length} tavoli</RowMeta>
                <RowActions>
                  <Btn onClick={() => setEditRoom(room)}>Modifica</Btn>
                  <Btn $variant="danger" onClick={() => handleDeleteRoom(room.id, room.name)}>Elimina</Btn>
                </RowActions>
              </>
            )}
          </Row>
        ))}
        <Form onSubmit={handleAddRoom}>
          <Input placeholder="Nome sala (es. Interno, Esterno, Bar…)" value={newRoom} onChange={e => setNewRoom(e.target.value)} />
          <Btn $variant="primary" type="submit" disabled={saving}>+ Aggiungi sala</Btn>
        </Form>
      </Card>

      {/* TAVOLI */}
      <Card>
        <CardHeader><CardTitle>🪑 Tavoli</CardTitle></CardHeader>
        {tables.length === 0 ? (
          <EmptyRow>Nessun tavolo configurato</EmptyRow>
        ) : tables.map(table => (
          <Row key={table.id}>
            {editTable?.id === table.id ? (
              <InlineEdit onSubmit={handleSaveTable}>
                <InlineInput autoFocus value={editTable.name} onChange={e => setEditTable({ ...editTable, name: e.target.value })} placeholder="Nome/numero" style={{ maxWidth: 120 }} />
                <InlineInput type="number" value={editTable.seats} onChange={e => setEditTable({ ...editTable, seats: parseInt(e.target.value) || 4 })} style={{ width: 60, flex: 'none' }} placeholder="Posti" />
                <Btn $variant="primary" type="submit">Salva</Btn>
                <Btn type="button" onClick={() => setEditTable(null)}>Annulla</Btn>
              </InlineEdit>
            ) : (
              <>
                <RowName style={{ opacity: table.is_active === 0 ? 0.5 : 1 }}>
                  {table.room_name} — Tavolo {table.name}
                </RowName>
                {table.is_active === 0 ? <InactiveBadge>Disattivato</InactiveBadge> : <RowMeta>{table.seats} posti</RowMeta>}
                <RowActions>
                  <Btn $variant="qr" title="Scarica QR" onClick={() => tableQrService.downloadQr(table.id, table.name)}>📲 QR</Btn>
                  <Btn onClick={() => setEditTable(table)}>Modifica</Btn>
                  {table.is_active === 0
                    ? <Btn $variant="primary" onClick={() => handleToggleTableActive(table)}>Attiva</Btn>
                    : <Btn $variant="warn" onClick={() => handleToggleTableActive(table)}>Disattiva</Btn>}
                </RowActions>
              </>
            )}
          </Row>
        ))}
        {rooms.length === 0 ? (
          <EmptyRow>Aggiungi prima una sala</EmptyRow>
        ) : (
          <Form onSubmit={handleAddTable}>
            <Select value={newTable.room_id} onChange={e => { setError(null); setNewTable({ ...newTable, room_id: e.target.value }); }}>
              <option value="">— Sala —</option>
              {rooms.map(r => <option key={r.id} value={String(r.id)}>{r.name}</option>)}
            </Select>
            <Input placeholder="Numero/nome tavolo (es. 1, 2, A…)" value={newTable.name} onChange={e => setNewTable({ ...newTable, name: e.target.value })} />
            <NumInput type="number" min="1" max="50" placeholder="Posti" value={newTable.seats} onChange={e => setNewTable({ ...newTable, seats: e.target.value })} />
            <Btn $variant="primary" type="submit" disabled={saving}>+ Aggiungi tavolo</Btn>
          </Form>
        )}
      </Card>
    </Page>
  );
}
